import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, ArrowLeft, Undo2, Clock, AlertTriangle } from "lucide-react";

export default function RecycleBinPage() {
  const { user } = useAuth();
  const isSuperAdmin = user && user.role === "super_admin";
  const [trips, setTrips] = useState([]);
  const [bills, setBills] = useState([]);
  const [retentionDays, setRetentionDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [purge, setPurge] = useState(null); // { kind: "trip"|"bill", id, code }
  const [purgeInput, setPurgeInput] = useState("");
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: b }] = await Promise.all([
        api.get("/trips-recycle-bin"),
        api.get("/bills-recycle-bin"),
      ]);
      setTrips(t.trips);
      setBills(b.bills);
      setRetentionDays(t.retention_days || 90);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (kind, id, code) => {
    const msg = kind === "trip"
      ? `Restore ${code}? It will go back to its last known status and appear in all lists again.`
      : `Restore bill ${code}? Its trips will be marked billed again.`;
    if (!window.confirm(msg)) return;
    const url = kind === "trip" ? `/trips-recycle-bin/${id}/restore` : `/bills-recycle-bin/${id}/restore`;
    try { await api.post(url); toast.success(`${code} restored`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openPurge = (kind, id, code) => { setPurge({ kind, id, code }); setPurgeInput(""); };

  const confirmPurge = async () => {
    if (!purge || purgeInput.trim() !== purge.code) return;
    setPurging(true);
    const url = purge.kind === "trip"
      ? `/trips-recycle-bin/${purge.id}/permanent`
      : `/bills-recycle-bin/${purge.id}/permanent`;
    try {
      await api.delete(url);
      toast.success(`${purge.code} permanently deleted`);
      setPurge(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPurging(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/superadmin" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back to Super Admin
        </Link>
        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-[#FF5A00]">RECYCLE BIN</div>
            <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="recycle-bin-page">Deleted items</h1>
            <p className="text-sm text-zinc-400 mt-1 flex items-center gap-2"><Clock className="w-4 h-4" /> Items are retained for {retentionDays} days before being purged.</p>
          </div>
        </div>

        <Tabs defaultValue="trips" className="mt-8">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="trips" data-testid="rb-tab-trips" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Trips ({trips.length})</TabsTrigger>
            <TabsTrigger value="bills" data-testid="rb-tab-bills" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Bills ({bills.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="trips" className="mt-6">
            <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">CN #</th>
                    <th className="text-left p-3">Route</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Freight</th>
                    <th className="text-left p-3">Deleted by · at</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map((t) => (
                    <tr key={t.id} data-testid={`recycle-row-${t.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60">
                      <td className="p-3 font-mono-ll text-xs">{t.cn_number}</td>
                      <td className="p-3 font-mono-ll text-xs">{t.source_city} → {t.destination_city}</td>
                      <td className="p-3 text-xs">{t.customer?.name || "—"}</td>
                      <td className="p-3">{formatINR(t.money?.customer_freight || 0)}</td>
                      <td className="p-3 text-xs text-zinc-400">
                        <div>{t.deleted_by_name || "—"}</div>
                        <div className="text-zinc-500 font-mono">{t.deleted_at ? new Date(t.deleted_at).toLocaleString("en-IN") : "—"}</div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => restore("trip", t.id, t.cn_number)}
                            data-testid={`restore-trip-${t.id}`}
                            className="h-8 rounded-sm border-emerald-500/40 bg-transparent text-emerald-400 hover:bg-emerald-500/10">
                            <Undo2 className="w-3 h-3 mr-1" /> Restore
                          </Button>
                          {isSuperAdmin && (
                            <Button size="sm" variant="outline" onClick={() => openPurge("trip", t.id, t.cn_number)}
                              data-testid={`purge-trip-${t.id}`}
                              className="h-8 rounded-sm border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10">
                              <Trash2 className="w-3 h-3 mr-1" /> Delete forever
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && trips.length === 0 && (
                    <tr><td colSpan={6} className="p-10 text-center text-zinc-500">
                      <Trash2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      No deleted trips. Nothing deleted in the last {retentionDays} days.
                    </td></tr>
                  )}
                  {loading && <tr><td colSpan={6} className="p-10 text-center text-zinc-500">Loading…</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="bills" className="mt-6">
            <div className="border border-[#2A2A2A] rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-3">Bill #</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Trips</th>
                    <th className="text-left p-3">Total</th>
                    <th className="text-left p-3">Deleted by · at</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((b) => (
                    <tr key={b.id} data-testid={`recycle-bill-row-${b.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60">
                      <td className="p-3 font-mono-ll text-xs">{b.bill_no}</td>
                      <td className="p-3 text-xs">{b.customer_name}</td>
                      <td className="p-3 text-xs">{b.trips?.length || 0}</td>
                      <td className="p-3">{formatINR(b.total || 0)}</td>
                      <td className="p-3 text-xs text-zinc-400">
                        <div>{b.deleted_by_name || "—"}</div>
                        <div className="text-zinc-500 font-mono">{b.deleted_at ? new Date(b.deleted_at).toLocaleString("en-IN") : "—"}</div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => restore("bill", b.id, b.bill_no)}
                            data-testid={`restore-bill-${b.id}`}
                            className="h-8 rounded-sm border-emerald-500/40 bg-transparent text-emerald-400 hover:bg-emerald-500/10">
                            <Undo2 className="w-3 h-3 mr-1" /> Restore
                          </Button>
                          {isSuperAdmin && (
                            <Button size="sm" variant="outline" onClick={() => openPurge("bill", b.id, b.bill_no)}
                              data-testid={`purge-bill-${b.id}`}
                              className="h-8 rounded-sm border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10">
                              <Trash2 className="w-3 h-3 mr-1" /> Delete forever
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && bills.length === 0 && (
                    <tr><td colSpan={6} className="p-10 text-center text-zinc-500">
                      <Trash2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      No deleted bills. Nothing deleted in the last {retentionDays} days.
                    </td></tr>
                  )}
                  {loading && <tr><td colSpan={6} className="p-10 text-center text-zinc-500">Loading…</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!purge} onOpenChange={(o) => { if (!o) setPurge(null); }}>
        <DialogContent data-testid="purge-dialog" className="bg-[#141414] border-[#2A2A2A] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-400">
              <AlertTriangle className="w-5 h-5" /> Permanently delete {purge?.code}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">
            This cannot be undone. The {purge?.kind === "bill" ? "bill" : "trip"} will be removed from the database immediately, without waiting for the {retentionDays}-day auto-purge.
          </p>
          <div className="mt-2">
            <label className="text-xs text-zinc-500 uppercase tracking-wider">Type <span className="font-mono text-zinc-300">{purge?.code}</span> to confirm</label>
            <Input
              data-testid="purge-confirm-input"
              value={purgeInput}
              onChange={(e) => setPurgeInput(e.target.value)}
              placeholder={purge?.code}
              className="mt-2 bg-[#0A0A0A] border-[#2A2A2A] font-mono"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurge(null)} data-testid="purge-cancel-btn"
              className="rounded-sm border-[#2A2A2A] bg-transparent hover:bg-white/5">Cancel</Button>
            <Button onClick={confirmPurge} disabled={purgeInput.trim() !== purge?.code || purging}
              data-testid="purge-confirm-btn"
              className="rounded-sm bg-rose-600 hover:bg-rose-500 text-white font-bold disabled:opacity-40">
              {purging ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
